import { AbstractBuildstep } from "@project/emerge";
import type { Sides } from "@project/emerge-types";
import * as THREE from "three";
import { EdgeType } from "../enum/EdgeType";
import { EndpointType } from "../enum/EndpointType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTPartType } from "../enum/PartType";
import type { CornerBraceType } from "../interfaces/CornerBrace";
import type { CornerBraceDefinition } from "../interfaces/CornerBraceProperties";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import { OptionDBInstance } from "../user_options/options_db";
import {
  computeAngleBarLengthAfterEachEndpoint,
  edgeReferenceLengthForBracketMm,
  resolveAngleSectionsForEdge,
} from "./angleBarEdgeMetrics";

// --- Dependency Data Interface ---
interface CornerBraceDependencyData {
  partId: string;
  groupName: string;
  buildstepName: string;
  partKind: "CornerBrace";
  type: LTPartType.CornerBrace;
  distanceFromPanel: number;
  partSelection: CornerBraceType;
  position: THREE.Vector3;
  side: Sides;
  top: boolean;
  orientation: number;
  edgeSides: Sides[];
  useLVariant: boolean;
}

// --- CornerBrace Build Step ---
export class CornerBraceBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  CornerBraceDefinition,
  CornerBraceDependencyData
> {
  constructor() {
    super("CornerBraceBuildStep");
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, CornerBraceDependencyData> {
    const dependencies = new Map<string, CornerBraceDependencyData>();
    let cornerBraceCounter = 0;

    for (const edge of blueprint.edges) {
      if (this.shouldSkipEdge(edge)) {
        continue;
      }

      const angleSections = resolveAngleSectionsForEdge(settings, edge);
      const { skipEdge, lengthAfterEndpoint } =
        computeAngleBarLengthAfterEachEndpoint({
          edge,
          settings,
          angleSections,
        });
      if (skipEdge) {
        continue;
      }

      for (let directionIndex = 0; directionIndex < 2; directionIndex++) {
        const dep = this.createCornerBraceForDirection(
          edge,
          settings,
          angleSections.strength,
          directionIndex,
          `cornerbrace_${edge.rects[0]?.id ?? "norect"}_${edge.index}_${directionIndex}_${cornerBraceCounter++}`,
          lengthAfterEndpoint[directionIndex] ?? lengthAfterEndpoint[0],
        );
        if (dep) {
          dependencies.set(dep.partId, dep);
        }
      }
    }

    return dependencies;
  }

  private shouldSkipEdge(edge: FlightCaseBlueprint["edges"][number]): boolean {
    return edge.type === EdgeType.FROMCUT || !edge.rects[0];
  }

  private createCornerBraceForDirection(
    edge: FlightCaseBlueprint["edges"][number],
    settings: FlightCaseSettings,
    distanceFromPanel: number,
    directionIndex: number,
    partId: string,
    effectiveAngleBarLength: number,
  ): CornerBraceDependencyData | null {
    const isReversedDirection = directionIndex === 1;
    const leftHandSide = edge.getLeftHandSide(isReversedDirection);
    const orientation = edge.getOrientation(leftHandSide) - 90;
    const endPoint = isReversedDirection
      ? edge.endPoints[1]
      : edge.endPoints[0];
    const startPoint = isReversedDirection
      ? edge.endPoints[0]
      : edge.endPoints[1];

    if (!endPoint) {
      return null;
    }
    if (endPoint.type === EndpointType.ON_CORNER) {
      return null;
    }

    // Check if we should skip because of L-variant corner
    // Only skip if: startPoint has L-variant corner AND edge length matches bracketLength exactly
    if (startPoint?.corner?.useLVariant && startPoint.corner.partSelection) {
      const cornerType = startPoint.corner.partSelection as
        | { angleVariant?: { bracketLength?: number } }
        | undefined;
      const angleVariant = cornerType?.angleVariant;

      if (angleVariant?.bracketLength !== undefined && edge.rects[0]) {
        const edgeLength = edgeReferenceLengthForBracketMm(edge, settings);
        const matchesBracketLength = edgeLength === angleVariant.bracketLength;

        if (matchesBracketLength) {
          return null;
        }
      }
    }

    const isDefaultGroup =
      (edge.rects[0]?.group ?? DefaultGroupName) === DefaultGroupName;
    const basePartSelection = isDefaultGroup
      ? settings.exteriorOptions.cornerBraces
      : settings.exteriorOptions.cornerBracesCover;

    const partSelection = this.resolveCornerBracePartSelection(
      basePartSelection,
      effectiveAngleBarLength,
    );

    const groupName = edge.rects[0]?.group ?? DefaultGroupName;

    return {
      partId,
      partKind: "CornerBrace",
      type: LTPartType.CornerBrace,
      groupName,
      buildstepName: this.buildstepName,
      distanceFromPanel,
      partSelection,
      position: endPoint.outerPos.clone(),
      side: leftHandSide,
      top: false,
      orientation,
      edgeSides: edge.sides,
      useLVariant: false,
    };
  }

  /**
   * Like CornerBuildStep + angleVariant meshes: long edge uses default mesh + long code,
   * short edge uses meshes.angleVariant + angleVariant.code.
   */
  private resolveCornerBracePartSelection(
    selection: CornerBraceType,
    effectiveAngleBarLength: number,
  ): CornerBraceType {
    const av = selection.angleVariant;
    const defaultMesh = selection.meshes?.default;
    const compactMesh = selection.meshes?.angleVariant;

    if (!av) {
      return selection;
    }

    const longCode =
      (typeof av.longCode === "string" && av.longCode.trim() !== ""
        ? av.longCode.trim()
        : undefined) ??
      (selection.name.includes("/")
        ? (selection.name.split("/")[0] ?? "").trim()
        : selection.name);

    const useCompact = effectiveAngleBarLength <= av.minLength;

    if (useCompact) {
      if (compactMesh) {
        const meta = this.lookupCornerBraceOption(av.code);
        return {
          ...selection,
          name: av.code,
          description: meta?.description ?? selection.description,
          meshes: { default: compactMesh },
          angleVariant: undefined,
        };
      }
      return OptionDBInstance.getByName<CornerBraceType>(
        LTCaseUserOption.CornerBraceType,
        av.code,
      );
    }

    if (defaultMesh) {
      const meta = this.lookupCornerBraceOption(longCode);
      return {
        ...selection,
        name: longCode,
        description: meta?.description ?? selection.description,
        meshes: { default: defaultMesh },
        angleVariant: undefined,
      };
    }

    return OptionDBInstance.getByName<CornerBraceType>(
      LTCaseUserOption.CornerBraceType,
      longCode,
    );
  }

  private lookupCornerBraceOption(
    code: string,
  ): Pick<CornerBraceType, "description" | "aliases"> | undefined {
    const found = OptionDBInstance.getAnyByName(code) as
      | CornerBraceType
      | undefined;
    if (found?.kind !== LTCaseUserOption.CornerBraceType) return undefined;
    return { description: found.description, aliases: found.aliases };
  }

  public createPart(
    partId: string,
    dependencies: CornerBraceDependencyData,
  ): CornerBraceDefinition {
    const finalRotation = this.rotateOntoSide(
      dependencies.side,
      dependencies.orientation,
    );

    const totalOffset = dependencies.distanceFromPanel;
    // Bestimme die beiden Richtungen für die Verschiebung basierend auf den Kanten-Seiten
    const offsetDirections = this.getEdgeOffsetDirections(
      dependencies.edgeSides,
    );

    // Verschiebe die Position in beide Richtungen
    const adjustedPosition = dependencies.position.clone();
    for (const direction of offsetDirections) {
      adjustedPosition.add(direction.multiplyScalar(totalOffset));
    }

    return {
      id: partId,
      type: dependencies.type,
      group: dependencies.groupName,
      buildstepName: dependencies.buildstepName,
      isSupplyPart: true,
      distanceFromPanel: dependencies.distanceFromPanel,
      partSelection: dependencies.partSelection,
      position: adjustedPosition,
      top: dependencies.top,
      orientation: dependencies.orientation,
      rotation: finalRotation,
    };
  }

  private getEdgeOffsetDirections(edgeSides: Sides[]): THREE.Vector3[] {
    // Verwende die bestehende RectPropertiesBySide Implementierung
    const directions: THREE.Vector3[] = [];

    for (const side of edgeSides) {
      const rectProps = RectPropertiesBySide[side];
      if (rectProps) {
        // Verwende die normale Richtung der Seite (nach außen)
        directions.push(rectProps.normalDir.clone());
      }
    }

    // Fallback falls keine Seiten gefunden wurden
    if (directions.length === 0) {
      directions.push(new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0));
    }

    return directions;
  }
}
